import React, { FC, MouseEvent, ReactNode, useCallback, useState } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import { EntityTypesSupportedColumnSelection } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { OrganizationMetaData } from "@mapmycustomers/shared/types/Organization";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { Popconfirm } from "@mapmycustomers/ui";

import MoreButton from "@app/component/MoreButton";
import showSaveSettingSuccessfulMessage from "@app/scene/settings/util/showSaveSettingSuccessfulMessage";
import { getDefaultColumns, isCurrentUserReadOnly } from "@app/store/iam";
import { updateDefaultColumns } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";

export interface ColumnsMenuProps {
  columns: ColumnModel<IField>;
  entityType: EntityTypesSupportedColumnSelection;
}

interface Props extends ColumnsMenuProps {
  defaultColumns: OrganizationMetaData["defaultColumns"];
  isReadOnlyMember: boolean;
  updateDefaultColumns: typeof updateDefaultColumns;
}

const ColumnsMenu: FC<Props> = ({
  columns,
  defaultColumns,
  entityType,
  isReadOnlyMember,
  updateDefaultColumns,
}) => {
  const intl = useIntl();

  const [menuVisibility, setMenuVisibility] = useState<boolean>(false);

  const handleConfirm = useCallback(
    (e?: MouseEvent<HTMLElement>) => {
      if (e) {
        stopEvents(e);
      }
      setMenuVisibility(false);

      const updatedDefaultColumns = {
        ...(defaultColumns || {}),
        [entityType]: columns
          .filter(({ visible }) => visible)
          .map(({ field }) => field.platformName),
      };
      updateDefaultColumns({
        callback: () => {
          showSaveSettingSuccessfulMessage(intl);
        },
        defaultColumns: updatedDefaultColumns,
      });
    },
    [columns, defaultColumns, entityType, intl, updateDefaultColumns]
  );

  return (
    <Dropdown
      onOpenChange={setMenuVisibility}
      open={menuVisibility}
      overlay={
        <Menu>
          <Popconfirm
            description={intl.formatMessage(
              {
                id: "component.columnsMenu.setAsOrgDefault.confirmation",
                defaultMessage:
                  "Selected columns will be the default for exports and list views <b>for the entire org</b>. Continue?",
                description:
                  "Popconfirm title for the SetAsOrgDefault menu item in the Columns Menu",
              },
              {
                b: (text: ReactNode[]) => <b>{text}</b>,
              }
            )}
            okText={intl.formatMessage({
              id: "component.columnsMenu.setAsOrgDefault.confirmation.ok",
              defaultMessage: "Continue",
              description:
                "Ok button title in the Popconfirm for the SetAsOrgDefault menu item in the Columns Menu",
            })}
            onCancel={(e?: MouseEvent<HTMLElement>) => {
              if (e) {
                stopEvents(e);
              }
              setMenuVisibility(false);
            }}
            onConfirm={handleConfirm}
          >
            <Menu.Item
              disabled={isReadOnlyMember}
              onClick={(menuInfo: MenuInfo) => {
                // keep menu opened when a Popconfirm is shown
                stopEvents(menuInfo.domEvent);
              }}
            >
              {intl.formatMessage({
                id: "component.columnsMenu.setAsOrgDefault",
                defaultMessage: "Set as org default",
                description: "Set as org default menu item in the Columns Menu",
              })}
            </Menu.Item>
          </Popconfirm>
        </Menu>
      }
      placement="bottomRight"
    >
      <MoreButton onClick={stopEvents} size="small" tooltipProps={{ title: null }} />
    </Dropdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  defaultColumns: getDefaultColumns(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
});

const mapDispatchToProps = {
  updateDefaultColumns: updateDefaultColumns,
};

export default connect(mapStateToProps, mapDispatchToProps)(ColumnsMenu);
